/**
 * SpaceImportWizard — CSV Import for Spaces (Legacy version)
 *
 * DROP INTO: src/components/Settings/SpaceImportWizard.jsx
 *
 * REQUIRES: npm install papaparse
 *
 * PROPS (passed from spaces.jsx):
 *   open, onClose, locationId, setSnackBar
 *   + props.firebase (injected by withFirebase HOC)
 */
import React, { useState, useCallback, useMemo } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  Select,
  Stack,
  Step,
  StepLabel,
  Stepper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import FileUploadIcon from '@mui/icons-material/FileUpload';
import DownloadIcon from '@mui/icons-material/Download';
import Papa from 'papaparse';
import { withFirebase } from '../Firebase';
import { spaceTypes } from '../../constants/data';

/* ------------------------------------------------------------------ */
/*  Constants                                                          */
/* ------------------------------------------------------------------ */

const STEPS = ['Upload CSV', 'Review & Resolve', 'Confirm & Import'];
const VALID_TYPES = Object.keys(spaceTypes);
const VALID_BOOLS = ['true', '1', 'yes', 'false', '0', 'no', ''];
const BOOL_TRUE = ['true', '1', 'yes'];
const USER_DATE_RE = /^\d{8}$/;
const HEX_COLOR_RE = /^#[0-9a-fA-F]{6}$/;

const parseBool = (val) => BOOL_TRUE.includes((val ?? '').trim().toLowerCase());

const isValidBool = (val) =>
  VALID_BOOLS.includes((val ?? '').trim().toLowerCase());

/** Validate a user-entered date in DDMMYYYY format */
const isValidUserDate = (val) => {
  const v = (val ?? '').trim();
  if (!v) return true;
  if (!USER_DATE_RE.test(v)) return false;
  const dd = parseInt(v.slice(0, 2), 10);
  const mm = parseInt(v.slice(2, 4), 10);
  const yyyy = parseInt(v.slice(4, 8), 10);
  if (mm < 1 || mm > 12 || dd < 1 || dd > 31) return false;
  const d = new Date(yyyy, mm - 1, dd);
  return d.getFullYear() === yyyy && d.getMonth() === mm - 1 && d.getDate() === dd;
};

/**
 * Convert DDMMYYYY (1-based month) to internal YYYYMMDD (zero-based month).
 * e.g. 01042026 (April 1st) → "20260301"
 */
const userDateToRef = (val) => {
  const v = (val ?? '').trim();
  if (!v) return '';
  const dd = v.slice(0, 2);
  const mm = parseInt(v.slice(2, 4), 10);
  const yyyy = v.slice(4, 8);
  const zeroMonth = String(mm - 1).padStart(2, '0');
  return `${yyyy}${zeroMonth}${dd}`;
};

/** Styling for a cell whose field has a validation error */
const errorCellSx = { color: 'error.dark', fontWeight: 'bold' };

/* ------------------------------------------------------------------ */
/*  Component                                                          */
/* ------------------------------------------------------------------ */

const SpaceImportWizardBase = (props) => {
  const { open, onClose, locationId, setSnackBar, firebase } = props;

  const [activeStep, setActiveStep] = useState(0);
  const [parsedRows, setParsedRows] = useState([]);
  const [parseError, setParseError] = useState(null);
  const [processedRows, setProcessedRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [importing, setImporting] = useState(false);
  const [importResults, setImportResults] = useState(null);

  /* ---- Reset ---- */
  const resetState = useCallback(() => {
    setActiveStep(0);
    setParsedRows([]);
    setParseError(null);
    setProcessedRows([]);
    setLoading(false);
    setImporting(false);
    setImportResults(null);
  }, []);

  const handleClose = () => {
    resetState();
    onClose();
  };

  /* ---------------------------------------------------------------- */
  /*  Step 0 — Upload & Parse                                         */
  /* ---------------------------------------------------------------- */

  const handleFileUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setParseError(null);

    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      complete: (results) => {
        if (results.errors.length > 0) {
          setParseError(
            `CSV parse error: ${results.errors[0].message} (row ${results.errors[0].row})`,
          );
          return;
        }
        const headers = results.meta.fields ?? [];
        if (!headers.includes('name') || !headers.includes('type')) {
          setParseError('CSV must contain at least "name" and "type" columns.');
          return;
        }
        setParsedRows(results.data);
      },
      error: (err) => setParseError(`Failed to read file: ${err.message}`),
    });
    e.target.value = '';
  };

  const handleDownloadTemplate = () => {
    const link = document.createElement('a');
    link.href = '/templates/spaces-template.csv';
    link.download = 'spaces-template.csv';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  /* ---- Validate & check duplicates (transition 0→1) ---- */
  const processRows = async () => {
    setLoading(true);
    try {
      const existingSnap = await firebase.db
        .collection(`location/${locationId}/spaces`)
        .where('archive', '==', false)
        .get();

      const existingByName = new Map();
      existingSnap.forEach((d) => {
        existingByName.set(d.data().name.trim().toLowerCase(), d.id);
      });

      const processed = parsedRows.map((raw, index) => {
        const errors = [];
        const fieldErrors = {};

        // Name (required)
        const name = raw.name?.trim();
        if (!name) {
          errors.push('Name is required');
          fieldErrors.name = 'Required';
        }

        // Type (required, must be valid)
        const type = raw.type?.trim().toLowerCase();
        if (!type) {
          errors.push('Type is required');
          fieldErrors.type = 'Required';
        } else if (!VALID_TYPES.includes(type)) {
          errors.push(`Invalid type "${raw.type}"`);
          fieldErrors.type = 'Invalid type';
        }

        // Seats
        if (raw.seats?.trim()) {
          const n = Number(raw.seats);
          if (isNaN(n) || n < 0 || !Number.isInteger(n)) {
            errors.push('Seats must be a whole number ≥ 0');
            fieldErrors.seats = 'Invalid number';
          }
        }

        // Boolean fields
        const boolFields = [
          { key: 'bookable', label: 'Bookable' },
          { key: 'allDay', label: 'All Day' },
          { key: 'hourly', label: 'Hourly' },
          { key: 'ignoreWhenChangingStatus', label: 'Ignore Status' },
        ];
        for (const { key, label } of boolFields) {
          if (raw[key]?.trim() && !isValidBool(raw[key])) {
            errors.push(`${label} must be true/false/yes/no/1/0`);
            fieldErrors[key] = 'Invalid boolean';
          }
        }

        // allDay and hourly are mutually exclusive
        if (parseBool(raw.allDay) && parseBool(raw.hourly)) {
          errors.push('A space cannot be both All Day and Hourly');
          fieldErrors.allDay = 'Cannot have both';
          fieldErrors.hourly = 'Cannot have both';
        }

        // Colour
        if (raw.backgroundColor?.trim() && !HEX_COLOR_RE.test(raw.backgroundColor.trim())) {
          errors.push('Colour must be a hex value like #ff0000');
          fieldErrors.backgroundColor = 'Invalid hex colour';
        }

        // Date fields
        if (!isValidUserDate(raw.bookableFrom)) {
          errors.push('Bookable From must be DDMMYYYY format');
          fieldErrors.bookableFrom = 'Use DDMMYYYY';
        }
        if (!isValidUserDate(raw.bookableUntil)) {
          errors.push('Bookable Until must be DDMMYYYY format');
          fieldErrors.bookableUntil = 'Use DDMMYYYY';
        }
        if (
          raw.bookableFrom?.trim() &&
          raw.bookableUntil?.trim() &&
          !fieldErrors.bookableFrom &&
          !fieldErrors.bookableUntil &&
          userDateToRef(raw.bookableFrom) > userDateToRef(raw.bookableUntil)
        ) {
          errors.push('Bookable From must be before Bookable Until');
          fieldErrors.bookableFrom = 'After Until date';
          fieldErrors.bookableUntil = 'Before From date';
        }

        const existingId = existingByName.get((name || '').toLowerCase()) ?? null;
        const status = errors.length ? 'error' : existingId ? 'duplicate' : 'new';

        return {
          index,
          raw,
          status,
          duplicateAction: 'skip',
          existingId,
          errors,
          fieldErrors,
          resolvedParentId: '',
        };
      });

      setProcessedRows(processed);
      setActiveStep(1);
    } finally {
      setLoading(false);
    }
  };

  /* ---------------------------------------------------------------- */
  /*  Step 1 — Review & Resolve                                       */
  /* ---------------------------------------------------------------- */

  const setDuplicateAction = (idx, action) => {
    setProcessedRows((prev) =>
      prev.map((r) => (r.index === idx ? { ...r, duplicateAction: action } : r)),
    );
  };

  const hasErrors = processedRows.some((r) => r.status === 'error');

  /* ---- Resolve parents (transition 1→2) ---- */
  const resolveParentsAndConfirm = async () => {
    setLoading(true);
    try {
      const existingSnap = await firebase.db
        .collection(`location/${locationId}/spaces`)
        .where('type', '==', 'area')
        .where('archive', '==', false)
        .get();

      const existingAreas = new Map();
      existingSnap.forEach((d) => {
        existingAreas.set(d.data().name.trim().toLowerCase(), d.id);
      });

      const csvAreas = new Set();
      processedRows.forEach((r) => {
        if (
          r.raw.type?.trim().toLowerCase() === 'area' &&
          (r.status === 'new' || (r.status === 'duplicate' && r.duplicateAction === 'update'))
        )
          csvAreas.add(r.raw.name.trim().toLowerCase());
      });

      const resolved = processedRows.map((r) => {
        const parentName = r.raw.parent?.trim();
        let resolvedParentId = '';
        if (parentName) {
          const key = parentName.toLowerCase();
          if (existingAreas.has(key)) {
            resolvedParentId = existingAreas.get(key);
          } else if (csvAreas.has(key)) {
            resolvedParentId = `__csv__${key}`;
          } else {
            if (r.status !== 'error') {
              r.errors.push(`Parent "${parentName}" not found in existing areas or CSV`);
              r.fieldErrors = { ...r.fieldErrors, parent: 'Not found' };
              r.status = 'error';
            }
          }
        }
        return { ...r, resolvedParentId };
      });

      setProcessedRows(resolved);
      setActiveStep(2);
    } finally {
      setLoading(false);
    }
  };

  /* ---------------------------------------------------------------- */
  /*  Step 2 — Preview order & computed stats                         */
  /* ---------------------------------------------------------------- */

  const orderedRows = useMemo(() => {
    const areas = processedRows.filter(
      (r) => r.raw.type?.trim().toLowerCase() === 'area' && r.status !== 'error',
    );
    const rest = processedRows.filter(
      (r) => r.raw.type?.trim().toLowerCase() !== 'area' && r.status !== 'error',
    );
    return [...areas, ...rest];
  }, [processedRows]);

  const stats = useMemo(() => {
    let toCreate = 0;
    let toUpdate = 0;
    let toSkip = 0;
    let errCount = 0;
    processedRows.forEach((r) => {
      if (r.status === 'error') errCount++;
      else if (r.status === 'new') toCreate++;
      else if (r.duplicateAction === 'update') toUpdate++;
      else toSkip++;
    });
    return { toCreate, toUpdate, toSkip, errCount };
  }, [processedRows]);

  /* ---- Import execution ---- */
  const executeImport = async () => {
    setImporting(true);
    const results = { created: 0, updated: 0, skipped: 0 };
    try {
      const spacesCol = firebase.db.collection(`location/${locationId}/spaces`);

      // Phase 1: Create areas first
      const areaRows = orderedRows.filter(
        (r) => r.raw.type?.trim().toLowerCase() === 'area',
      );
      const nonAreaRows = orderedRows.filter(
        (r) => r.raw.type?.trim().toLowerCase() !== 'area',
      );

      const areaNameToId = new Map();

      for (let i = 0; i < areaRows.length; i += 500) {
        const chunk = areaRows.slice(i, i + 500);
        const batch = firebase.db.batch();
        const pendingCreates = [];

        for (const row of chunk) {
          if (row.status === 'duplicate' && row.duplicateAction === 'skip') {
            if (row.existingId)
              areaNameToId.set(row.raw.name.trim().toLowerCase(), row.existingId);
            results.skipped++;
            continue;
          }

          const spaceData = buildSpaceData(row, '');
          if (row.status === 'duplicate' && row.existingId) {
            batch.set(
              firebase.db.doc(`location/${locationId}/spaces/${row.existingId}`),
              spaceData,
              { merge: true },
            );
            areaNameToId.set(row.raw.name.trim().toLowerCase(), row.existingId);
            results.updated++;
          } else {
            const newRef = spacesCol.doc();
            batch.set(newRef, spaceData);
            pendingCreates.push({
              name: row.raw.name.trim().toLowerCase(),
              ref: newRef,
            });
            results.created++;
          }
        }
        await batch.commit();

        for (const { name, ref } of pendingCreates) {
          areaNameToId.set(name, ref.id);
        }
      }

      // Also map any existing areas that weren't in the CSV
      const existingAreaSnap = await firebase.db
        .collection(`location/${locationId}/spaces`)
        .where('type', '==', 'area')
        .where('archive', '==', false)
        .get();

      existingAreaSnap.forEach((d) => {
        const name = d.data().name?.trim().toLowerCase();
        if (name && !areaNameToId.has(name)) {
          areaNameToId.set(name, d.id);
        }
      });

      // Phase 2: Create non-area spaces
      for (let i = 0; i < nonAreaRows.length; i += 500) {
        const chunk = nonAreaRows.slice(i, i + 500);
        const batch = firebase.db.batch();

        for (const row of chunk) {
          if (row.status === 'duplicate' && row.duplicateAction === 'skip') {
            results.skipped++;
            continue;
          }

          let parentId = row.resolvedParentId;
          if (parentId.startsWith('__csv__')) {
            const parentKey = parentId.replace('__csv__', '');
            parentId = areaNameToId.get(parentKey) ?? '';
          }

          const spaceData = buildSpaceData(row, parentId);
          if (row.status === 'duplicate' && row.existingId) {
            batch.set(
              firebase.db.doc(`location/${locationId}/spaces/${row.existingId}`),
              spaceData,
              { merge: true },
            );
            results.updated++;
          } else {
            batch.set(spacesCol.doc(), spaceData);
            results.created++;
          }
        }
        await batch.commit();
      }

      setImportResults(results);
      setSnackBar({
        display: true,
        type: 'success',
        message: `Import complete: ${results.created} created, ${results.updated} updated, ${results.skipped} skipped`,
      });
    } catch (err) {
      console.error('[SpaceImportWizard] Import error:', err);
      setSnackBar({
        display: true,
        type: 'error',
        message: `Import failed: ${err instanceof Error ? err.message : 'Unknown error'}`,
      });
    } finally {
      setImporting(false);
    }
  };

  /* ---------------------------------------------------------------- */
  /*  Helpers                                                          */
  /* ---------------------------------------------------------------- */

  const buildSpaceData = (row, parentId) => ({
    name: row.raw.name.trim(),
    description: row.raw.description?.trim() ?? '',
    seats: row.raw.seats ? Number(row.raw.seats) : 0,
    type: row.raw.type.trim().toLowerCase(),
    parent: parentId,
    bookable: parseBool(row.raw.bookable),
    timePeriod: {
      allDay: parseBool(row.raw.allDay),
      ampm: false,
      hourly: parseBool(row.raw.hourly),
    },
    approvalRequired: false,
    amenities: row.raw.amenities
      ? row.raw.amenities.split(',').map((a) => a.trim()).filter(Boolean)
      : [],
    archive: false,
    backgroundColor: row.raw.backgroundColor?.trim() || '#ffffff',
    ignoreWhenChangingStatus: parseBool(row.raw.ignoreWhenChangingStatus),
    bookableFrom: userDateToRef(row.raw.bookableFrom ?? ''),
    bookableUntil: userDateToRef(row.raw.bookableUntil ?? ''),
  });

  const statusChip = (row) => {
    if (row.status === 'error')
      return <Chip label="Error" color="error" size="small" />;
    if (row.status === 'duplicate')
      return <Chip label="Duplicate" color="warning" size="small" />;
    return <Chip label="New" color="success" size="small" />;
  };

  /* ---------------------------------------------------------------- */
  /*  Render                                                           */
  /* ---------------------------------------------------------------- */

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{ sx: { minHeight: '60vh' } }}
    >
      <DialogTitle>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">Import Spaces from CSV</Typography>
          <IconButton onClick={handleClose} size="small">
            <CloseIcon />
          </IconButton>
        </Stack>
        <Stepper activeStep={activeStep} sx={{ mt: 2 }}>
          {STEPS.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>
      </DialogTitle>

      <DialogContent dividers>
        {/* ---- Step 0: Upload ---- */}
        {activeStep === 0 && (
          <Box>
            <Stack spacing={2} sx={{ py: 2 }}>
              <Typography variant="body2">
                Upload a CSV file to bulk-import spaces. The CSV should contain
                columns for each space field. Areas (parent spaces) will be
                created first so child spaces can reference them.
              </Typography>
              <Stack direction="row" spacing={2}>
                <Button
                  variant="outlined"
                  startIcon={<DownloadIcon />}
                  onClick={handleDownloadTemplate}
                >
                  Download Template
                </Button>
                <Button
                  variant="contained"
                  component="label"
                  startIcon={<FileUploadIcon />}
                >
                  Upload CSV
                  <input type="file" accept=".csv" hidden onChange={handleFileUpload} />
                </Button>
              </Stack>
              {parseError && <Alert severity="error">{parseError}</Alert>}
              {parsedRows.length > 0 && (
                <Alert severity="success">
                  Parsed {parsedRows.length} rows successfully.
                </Alert>
              )}
              {parsedRows.length > 0 && (
                <TableContainer sx={{ maxHeight: 300 }}>
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 'bold' }}>Name</TableCell>
                        <TableCell sx={{ fontWeight: 'bold' }}>Type</TableCell>
                        <TableCell sx={{ fontWeight: 'bold' }}>Seats</TableCell>
                        <TableCell sx={{ fontWeight: 'bold' }}>Parent</TableCell>
                        <TableCell sx={{ fontWeight: 'bold' }}>Bookable</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {parsedRows.map((row, i) => (
                        <TableRow key={i}>
                          <TableCell>{row.name}</TableCell>
                          <TableCell>{row.type}</TableCell>
                          <TableCell>{row.seats}</TableCell>
                          <TableCell>{row.parent}</TableCell>
                          <TableCell>{row.bookable}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Stack>
          </Box>
        )}

        {/* ---- Step 1: Review & Resolve ---- */}
        {activeStep === 1 && (
          <Box>
            <Stack spacing={1} sx={{ mb: 2 }}>
              <Typography variant="body2">
                Review each row. Duplicates (matching name) can be updated or skipped.
              </Typography>
              {hasErrors && (
                <Alert severity="warning">
                  Some rows have validation errors and will not be imported.
                </Alert>
              )}
            </Stack>
            <TableContainer sx={{ maxHeight: 400, overflowX: 'auto' }}>
              <Table size="small" stickyHeader sx={{ minWidth: 1400 }}>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 'bold' }}>#</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Status</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Action</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Name</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Description</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Type</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Seats</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Parent</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Bookable</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>All Day</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Hourly</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Amenities</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Colour</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Ignore Status</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Bookable From</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Bookable Until</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Issues</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {processedRows.map((row) => {
                    const fe = row.fieldErrors;
                    return (
                      <TableRow
                        key={row.index}
                        sx={{
                          bgcolor:
                            row.status === 'error'
                              ? '#fce4ec'
                              : row.status === 'duplicate'
                                ? 'warning.light'
                                : 'success.light',
                        }}
                      >
                        <TableCell>{row.index + 1}</TableCell>
                        <TableCell>{statusChip(row)}</TableCell>
                        <TableCell>
                          {row.status === 'duplicate' ? (
                            <Select
                              size="small"
                              value={row.duplicateAction}
                              onChange={(e) => setDuplicateAction(row.index, e.target.value)}
                            >
                              <MenuItem value="skip">Skip</MenuItem>
                              <MenuItem value="update">Update</MenuItem>
                            </Select>
                          ) : row.status === 'error' ? (
                            '—'
                          ) : (
                            'Create'
                          )}
                        </TableCell>
                        <TableCell sx={fe.name ? errorCellSx : undefined}>
                          {row.raw.name || '—'}
                        </TableCell>
                        <TableCell
                          sx={{
                            maxWidth: 200,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {row.raw.description || '—'}
                        </TableCell>
                        <TableCell sx={fe.type ? errorCellSx : undefined}>
                          {fe.type
                            ? row.raw.type || '—'
                            : (spaceTypes[row.raw.type.trim().toLowerCase()] ?? row.raw.type)}
                        </TableCell>
                        <TableCell sx={fe.seats ? errorCellSx : undefined}>
                          {row.raw.seats || '—'}
                        </TableCell>
                        <TableCell sx={fe.parent ? errorCellSx : undefined}>
                          {row.raw.parent || '—'}
                        </TableCell>
                        <TableCell sx={fe.bookable ? errorCellSx : undefined}>
                          {row.raw.bookable || '—'}
                        </TableCell>
                        <TableCell sx={fe.allDay ? errorCellSx : undefined}>
                          {row.raw.allDay || '—'}
                        </TableCell>
                        <TableCell sx={fe.hourly ? errorCellSx : undefined}>
                          {row.raw.hourly || '—'}
                        </TableCell>
                        <TableCell
                          sx={{
                            maxWidth: 150,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {row.raw.amenities || '—'}
                        </TableCell>
                        <TableCell sx={fe.backgroundColor ? errorCellSx : undefined}>
                          {row.raw.backgroundColor ? (
                            fe.backgroundColor ? (
                              row.raw.backgroundColor
                            ) : (
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <Box
                                  sx={{
                                    width: 16,
                                    height: 16,
                                    borderRadius: '2px',
                                    bgcolor: row.raw.backgroundColor,
                                    border: '1px solid',
                                    borderColor: 'divider',
                                    flexShrink: 0,
                                  }}
                                />
                                <Typography variant="caption">
                                  {row.raw.backgroundColor}
                                </Typography>
                              </Box>
                            )
                          ) : (
                            '—'
                          )}
                        </TableCell>
                        <TableCell
                          sx={fe.ignoreWhenChangingStatus ? errorCellSx : undefined}
                        >
                          {row.raw.ignoreWhenChangingStatus || '—'}
                        </TableCell>
                        <TableCell sx={fe.bookableFrom ? errorCellSx : undefined}>
                          {row.raw.bookableFrom || '—'}
                        </TableCell>
                        <TableCell sx={fe.bookableUntil ? errorCellSx : undefined}>
                          {row.raw.bookableUntil || '—'}
                        </TableCell>
                        <TableCell>
                          {row.errors.length > 0 && (
                            <Typography variant="caption" color="error">
                              {row.errors.join('; ')}
                            </Typography>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        )}

        {/* ---- Step 2: Confirm & Import ---- */}
        {activeStep === 2 && (
          <Box>
            {importResults ? (
              <Stack spacing={2} sx={{ py: 2 }}>
                <Alert severity="success">Import complete!</Alert>
                <Typography>
                  Created: <strong>{importResults.created}</strong>
                </Typography>
                <Typography>
                  Updated: <strong>{importResults.updated}</strong>
                </Typography>
                <Typography>
                  Skipped: <strong>{importResults.skipped}</strong>
                </Typography>
              </Stack>
            ) : (
              <Stack spacing={2} sx={{ py: 2 }}>
                <Typography variant="body2">
                  Spaces will be imported in this order: areas first, then child spaces.
                </Typography>
                <Stack direction="row" spacing={3} sx={{ mb: 1 }}>
                  <Typography variant="body2">
                    To create: <strong>{stats.toCreate}</strong>
                  </Typography>
                  <Typography variant="body2">
                    To update: <strong>{stats.toUpdate}</strong>
                  </Typography>
                  <Typography variant="body2">
                    Skipping: <strong>{stats.toSkip}</strong>
                  </Typography>
                  {stats.errCount > 0 && (
                    <Typography variant="body2" color="error">
                      Errors: <strong>{stats.errCount}</strong>
                    </Typography>
                  )}
                </Stack>
                <TableContainer sx={{ maxHeight: 350, overflowX: 'auto' }}>
                  <Table size="small" stickyHeader sx={{ minWidth: 1400 }}>
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 'bold' }}>Order</TableCell>
                        <TableCell sx={{ fontWeight: 'bold' }}>Action</TableCell>
                        <TableCell sx={{ fontWeight: 'bold' }}>Name</TableCell>
                        <TableCell sx={{ fontWeight: 'bold' }}>Description</TableCell>
                        <TableCell sx={{ fontWeight: 'bold' }}>Type</TableCell>
                        <TableCell sx={{ fontWeight: 'bold' }}>Seats</TableCell>
                        <TableCell sx={{ fontWeight: 'bold' }}>Parent</TableCell>
                        <TableCell sx={{ fontWeight: 'bold' }}>Bookable</TableCell>
                        <TableCell sx={{ fontWeight: 'bold' }}>All Day</TableCell>
                        <TableCell sx={{ fontWeight: 'bold' }}>Hourly</TableCell>
                        <TableCell sx={{ fontWeight: 'bold' }}>Amenities</TableCell>
                        <TableCell sx={{ fontWeight: 'bold' }}>Colour</TableCell>
                        <TableCell sx={{ fontWeight: 'bold' }}>Ignore Status</TableCell>
                        <TableCell sx={{ fontWeight: 'bold' }}>Bookable From</TableCell>
                        <TableCell sx={{ fontWeight: 'bold' }}>Bookable Until</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {orderedRows.map((row, i) => {
                        const action =
                          row.status === 'duplicate'
                            ? row.duplicateAction === 'skip'
                              ? 'Skip'
                              : 'Update'
                            : 'Create';
                        return (
                          <TableRow key={row.index}>
                            <TableCell>{i + 1}</TableCell>
                            <TableCell>
                              <Chip
                                label={action}
                                size="small"
                                color={
                                  action === 'Create'
                                    ? 'success'
                                    : action === 'Update'
                                      ? 'warning'
                                      : 'default'
                                }
                              />
                            </TableCell>
                            <TableCell>{row.raw.name}</TableCell>
                            <TableCell
                              sx={{
                                maxWidth: 200,
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                              }}
                            >
                              {row.raw.description || '—'}
                            </TableCell>
                            <TableCell>
                              {spaceTypes[row.raw.type.trim().toLowerCase()] ?? row.raw.type}
                            </TableCell>
                            <TableCell>{row.raw.seats || '—'}</TableCell>
                            <TableCell>{row.raw.parent || '—'}</TableCell>
                            <TableCell>
                              {parseBool(row.raw.bookable) ? 'Yes' : 'No'}
                            </TableCell>
                            <TableCell>
                              {parseBool(row.raw.allDay) ? 'Yes' : 'No'}
                            </TableCell>
                            <TableCell>
                              {parseBool(row.raw.hourly) ? 'Yes' : 'No'}
                            </TableCell>
                            <TableCell
                              sx={{
                                maxWidth: 150,
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                              }}
                            >
                              {row.raw.amenities || '—'}
                            </TableCell>
                            <TableCell>
                              {row.raw.backgroundColor ? (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                  <Box
                                    sx={{
                                      width: 16,
                                      height: 16,
                                      borderRadius: '2px',
                                      bgcolor: row.raw.backgroundColor,
                                      border: '1px solid',
                                      borderColor: 'divider',
                                      flexShrink: 0,
                                    }}
                                  />
                                  <Typography variant="caption">
                                    {row.raw.backgroundColor}
                                  </Typography>
                                </Box>
                              ) : (
                                '—'
                              )}
                            </TableCell>
                            <TableCell>
                              {parseBool(row.raw.ignoreWhenChangingStatus) ? 'Yes' : 'No'}
                            </TableCell>
                            <TableCell>{row.raw.bookableFrom || '—'}</TableCell>
                            <TableCell>{row.raw.bookableUntil || '—'}</TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Stack>
            )}
          </Box>
        )}

        {loading && (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              p: 4,
            }}
          >
            <CircularProgress />
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        {activeStep === 0 && (
          <Button
            variant="contained"
            onClick={processRows}
            disabled={parsedRows.length === 0 || loading}
          >
            Next
          </Button>
        )}
        {activeStep === 1 && (
          <>
            <Button onClick={() => setActiveStep(0)}>Back</Button>
            <Button
              variant="contained"
              onClick={resolveParentsAndConfirm}
              disabled={loading}
            >
              Next
            </Button>
          </>
        )}
        {activeStep === 2 && !importResults && (
          <>
            <Button onClick={() => setActiveStep(1)} disabled={importing}>
              Back
            </Button>
            <Button
              variant="contained"
              onClick={executeImport}
              disabled={importing || (stats.toCreate === 0 && stats.toUpdate === 0)}
              startIcon={importing ? <CircularProgress size={16} /> : <FileUploadIcon />}
            >
              {importing ? 'Importing…' : 'Import'}
            </Button>
          </>
        )}
        {activeStep === 2 && importResults && (
          <Button variant="contained" onClick={handleClose}>
            Done
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default withFirebase(SpaceImportWizardBase);
